import React, { useEffect, useState, useRef } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Loader2, ChevronLeft, ChevronRight, Calendar, MapPin, Sparkles, Lock, KeyRound, ArrowLeft, Sun, Maximize, RotateCcw } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DigitalWallet() {
  const { orderId } = useParams();
  const [params] = useSearchParams();
  const token = params.get('t');
  const claim = params.get('claim') === '1';
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeIdx, setActiveIdx] = useState(0);
  const [flipped, setFlipped] = useState(false);
  const [showSetPwd, setShowSetPwd] = useState(claim);
  const [pwd, setPwd] = useState('');
  const [savingPwd, setSavingPwd] = useState(false);
  const dragX = useRef(0);
  const { setSession } = useAuth();

  useEffect(() => {
    if (!token) { setError('Missing wallet token. Re-open the link from your confirmation email.'); setLoading(false); return; }
    (async () => {
      try {
        const res = await axios.get(`${API}/wallet/${orderId}`, { params: { t: token } });
        setData(res.data);
        // Persist auth via AuthContext so user stays logged in next time they hit /my-tickets
        try {
          const auth = await axios.post(`${API}/wallet/${orderId}/auto-login`, { token });
          if (auth.data?.token) {
            setSession(auth.data.token, auth.data.user);
          }
        } catch { /* non-fatal */ }
      } catch (e) {
        setError(e.response?.data?.detail || 'Could not load wallet');
      } finally { setLoading(false); }
    })();
  }, [orderId, token]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (!data?.tickets?.length) return;
      if (e.key === 'ArrowLeft') setActiveIdx(i => Math.max(0, i - 1));
      else if (e.key === 'ArrowRight') setActiveIdx(i => Math.min(data.tickets.length - 1, i + 1));
      else if (e.key === ' ' || e.key === 'Enter') setFlipped(f => !f);
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [data]);

  const setPassword = async () => {
    if (pwd.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    setSavingPwd(true);
    try {
      await axios.post(`${API}/wallet/${orderId}/set-password`, { token, password: pwd });
      toast.success('Password saved — you can now log in with email + password');
      setShowSetPwd(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save password');
    } finally { setSavingPwd(false); }
  };

  if (loading) return <div className="min-h-screen bg-black flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-indigo-400" /></div>;

  if (error) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center p-6">
        <div className="text-center max-w-md">
          <Lock className="h-12 w-12 mx-auto text-rose-500/60 mb-3" />
          <h1 className="text-2xl font-bold mb-2">Can't open this wallet</h1>
          <p className="text-slate-400 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  const { order, tickets, event } = data;
  const ticket = tickets[activeIdx];
  const total = tickets.length;
  const eventName = event?.name || event?.title || 'Eventism Event';
  const eventDate = event?.event_date || event?.date || '';
  const eventTime = event?.event_time || event?.time || '';
  const venueName = event?.venue_name || event?.venue?.name || '';
  const heroImage = event?.image_url || event?.hero_image;
  const eventCity = event?.city;
  const eventSlug = event?.slug;

  return (
    <div
      className="min-h-screen w-full text-white relative overflow-hidden"
      style={{ background: 'radial-gradient(ellipse at top, #1e1b4b 0%, #020617 60%, #000 100%)' }}
      data-testid="digital-wallet-page"
    >
      {/* Animated background glow */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        <motion.div
          animate={{ scale: [1, 1.3, 1], opacity: [0.4, 0.7, 0.4] }}
          transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(168,85,247,0.35), transparent 70%)' }}
        />
        <motion.div
          animate={{ scale: [1.2, 1, 1.2], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full"
          style={{ background: 'radial-gradient(circle, rgba(99,102,241,0.4), transparent 70%)' }}
        />
      </div>

      {/* Top bar */}
      <div className="relative z-10 flex items-center justify-between px-5 py-4">
        <button onClick={() => navigate('/my-tickets')} className="flex items-center gap-2 text-sm text-slate-300 hover:text-white" data-testid="wallet-back-btn">
          <ArrowLeft className="h-4 w-4" /> My Tickets
        </button>
        <div className="text-center">
          <p className="text-[10px] uppercase tracking-[0.3em] text-indigo-300 font-bold">EVENTISM</p>
          <p className="text-[11px] text-slate-400 mt-0.5">Digital Wallet</p>
        </div>
        <button
          onClick={() => { try { document.documentElement.requestFullscreen?.(); } catch {} }}
          className="text-slate-400 hover:text-white"
          aria-label="Fullscreen"
        ><Maximize className="h-4 w-4" /></button>
      </div>

      <div className="relative z-10 max-w-md mx-auto px-4 pb-8">
        {/* Brightness tip */}
        <p className="text-center text-[11px] text-amber-300/80 mb-3 flex items-center justify-center gap-1.5"><Sun className="h-3 w-3" /> Turn brightness up at the door for a faster scan</p>

        {/* Ticket card with swipe + flip */}
        <div className="relative" style={{ perspective: '1500px' }}>
          <AnimatePresence mode="wait">
            <motion.div
              key={ticket.ticket_code}
              initial={{ opacity: 0, x: 100, rotateY: 0 }}
              animate={{ opacity: 1, x: 0, rotateY: flipped ? 180 : 0 }}
              exit={{ opacity: 0, x: -100 }}
              transition={{ duration: 0.45, ease: 'easeOut' }}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.2}
              onDrag={(_, info) => { dragX.current = info.offset.x; }}
              onDragEnd={() => {
                if (dragX.current < -80 && activeIdx < total - 1) setActiveIdx(activeIdx + 1);
                if (dragX.current > 80 && activeIdx > 0) setActiveIdx(activeIdx - 1);
                dragX.current = 0;
              }}
              onClick={(e) => {
                // flip only when not dragging
                if (Math.abs(dragX.current) < 5) setFlipped(f => !f);
              }}
              className="relative cursor-pointer"
              style={{ transformStyle: 'preserve-3d' }}
              data-testid={`ticket-card-${ticket.ticket_code}`}
            >
              {/* FRONT */}
              <div
                className="rounded-3xl overflow-hidden shadow-2xl border-2 border-indigo-500/40"
                style={{
                  background: 'linear-gradient(135deg, #4f46e5 0%, #7c3aed 50%, #ec4899 100%)',
                  boxShadow: '0 0 60px rgba(99,102,241,0.55), 0 0 120px rgba(168,85,247,0.35)',
                  backfaceVisibility: 'hidden',
                }}
              >
                {/* Hero image strip */}
                {heroImage && (
                  <div className="relative h-32 overflow-hidden">
                    <img src={heroImage} alt="" className="w-full h-full object-cover opacity-90" />
                    <div className="absolute inset-0 bg-gradient-to-t from-indigo-700/90 via-purple-700/40 to-transparent" />
                  </div>
                )}
                <div className="px-6 pt-5 pb-2">
                  <p className="text-[10px] uppercase tracking-[0.3em] text-indigo-200 font-bold">EVENTISM</p>
                  <h1 className="text-xl font-bold mt-1 leading-tight" data-testid="wallet-event-name">{eventName}</h1>
                  <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-indigo-100/90">
                    {eventDate && <span className="flex items-center gap-1"><Calendar className="h-3 w-3" />{eventDate}{eventTime ? ` · ${eventTime}` : ''}</span>}
                    {venueName && <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{venueName}</span>}
                  </div>
                </div>

                {/* Perforation */}
                <div className="relative my-2 h-6">
                  <div className="absolute inset-x-0 top-1/2 border-t-2 border-dashed border-white/40" />
                  <div className="absolute -left-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-[#020617]" />
                  <div className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-[#020617]" />
                </div>

                {/* QR area */}
                <div className="bg-white px-6 py-6 text-slate-900 text-center">
                  <div className="inline-block bg-white rounded-xl p-3 shadow-lg ring-1 ring-slate-200">
                    {ticket.qr_code_base64 ? (
                      <img src={`data:image/png;base64,${ticket.qr_code_base64}`} alt="ticket QR" className="w-56 h-56" data-testid="wallet-qr" />
                    ) : (
                      <div className="w-56 h-56 bg-slate-100 flex items-center justify-center text-xs text-slate-400">QR unavailable</div>
                    )}
                  </div>
                  <p className="font-mono text-2xl font-bold tracking-widest mt-3 text-indigo-600" data-testid="wallet-code">{ticket.ticket_code}</p>
                  <p className="text-[10px] text-slate-500 uppercase tracking-wider mt-1">Show this code at the door</p>
                </div>

                {/* Footer band */}
                <div className="px-6 py-3 bg-black/30 flex items-center justify-between text-xs">
                  <div>
                    <p className="text-indigo-200/80">{ticket.ticket_type_name || 'General Admission'}</p>
                    <p className="text-white font-medium">{ticket.buyer_name || order.buyer_name}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-indigo-200/80">Ticket</p>
                    <p className="text-white font-bold">{activeIdx + 1} / {total}</p>
                  </div>
                </div>
              </div>

              {/* BACK */}
              <div
                className="absolute inset-0 rounded-3xl overflow-hidden border-2 border-indigo-500/40 bg-slate-900/95 backdrop-blur p-6 flex flex-col"
                style={{ backfaceVisibility: 'hidden', transform: 'rotateY(180deg)' }}
              >
                <p className="text-[10px] uppercase tracking-[0.3em] text-indigo-300 font-bold flex items-center gap-1.5"><RotateCcw className="h-3 w-3" /> Ticket details</p>
                <div className="mt-4 space-y-3 text-sm flex-1">
                  <Row label="Event" value={eventName} />
                  <Row label="Date" value={`${eventDate}${eventTime ? ` · ${eventTime}` : ''}`} />
                  <Row label="Venue" value={venueName || '—'} />
                  <Row label="Type" value={ticket.ticket_type_name} />
                  <Row label="Holder" value={ticket.buyer_name || order.buyer_name} />
                  <Row label="Code" value={<span className="font-mono">{ticket.ticket_code}</span>} />
                  <Row label="Order" value={`#${order.order_id}`} />
                  <Row label="Total paid" value={`$${(order.total_amount || 0).toFixed(2)}`} />
                  <Row label="Status" value={<span className="text-emerald-400 capitalize">{ticket.status}</span>} />
                </div>
                {eventCity && eventSlug && (
                  <Link to={`/events/${eventCity}/${eventSlug}`} className="mt-4 text-xs text-indigo-300 hover:text-indigo-200 underline-offset-2 hover:underline">
                    View event page →
                  </Link>
                )}
                <p className="text-[10px] text-slate-500 mt-3 text-center">Tap card to flip back</p>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Nav dots + arrows */}
          {total > 1 && (
            <>
              <button
                onClick={() => setActiveIdx(i => Math.max(0, i - 1))}
                disabled={activeIdx === 0}
                className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-12 p-2 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur disabled:opacity-30 disabled:cursor-not-allowed hidden sm:block"
                data-testid="wallet-prev"
              ><ChevronLeft className="h-5 w-5" /></button>
              <button
                onClick={() => setActiveIdx(i => Math.min(total - 1, i + 1))}
                disabled={activeIdx === total - 1}
                className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-12 p-2 rounded-full bg-white/10 hover:bg-white/20 backdrop-blur disabled:opacity-30 disabled:cursor-not-allowed hidden sm:block"
                data-testid="wallet-next"
              ><ChevronRight className="h-5 w-5" /></button>
            </>
          )}
        </div>

        {/* Pagination dots */}
        {total > 1 && (
          <div className="flex items-center justify-center gap-2 mt-5" data-testid="wallet-dots">
            {tickets.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveIdx(i)}
                className={`h-2 rounded-full transition-all ${i === activeIdx ? 'w-8 bg-indigo-400' : 'w-2 bg-white/20'}`}
                aria-label={`Ticket ${i + 1}`}
              />
            ))}
          </div>
        )}

        <p className="text-center text-[11px] text-slate-500 mt-4">Swipe / arrow keys to switch · Tap card to flip</p>

        {/* Set password card (claim account) */}
        {showSetPwd && (
          <div className="mt-8 bg-slate-900/80 backdrop-blur border border-indigo-500/30 rounded-2xl p-5 space-y-3" data-testid="wallet-set-password">
            <p className="text-sm font-semibold flex items-center gap-2"><KeyRound className="h-4 w-4 text-indigo-400" />Set a password (optional)</p>
            <p className="text-xs text-slate-400">We auto-created a passwordless account. Add a password and you can log in from any device.</p>
            <div className="flex gap-2">
              <input type="password" placeholder="Min 6 characters" value={pwd} onChange={e => setPwd(e.target.value)} className="flex-1 h-10 px-3 rounded-md bg-slate-800 border border-slate-700 text-sm" />
              <Button onClick={setPassword} disabled={savingPwd} className="bg-indigo-600 hover:bg-indigo-700">
                {savingPwd ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-baseline justify-between gap-3 border-b border-slate-800 pb-2">
      <span className="text-[11px] uppercase tracking-wider text-slate-500">{label}</span>
      <span className="text-right text-slate-200">{value}</span>
    </div>
  );
}
